import { Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { BrowserRingAvailabilityControl } from '@apps/web/components/voice_calls/browser_ring_availability_control';
import {
    type BrowserCallStatus,
    type IncomingBrowserCall,
    useCall,
} from '@apps/web/context/call_provider';
import { formatPhoneNumber } from '@apps/web/lib/phone_number_formatting';

function formatDuration(durationS: number): string {
    const minutes = Math.floor(durationS / 60);
    const seconds = durationS % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function useCallDurationS(startedAtMs: number | null): number {
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        if (startedAtMs === null) {
            return;
        }
        const interval = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [startedAtMs]);

    return startedAtMs === null
        ? 0
        : Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
}

type InCallPanelViewProps = {
    readonly status: BrowserCallStatus;
    readonly caregiverName: string | null;
    readonly durationS: number;
    readonly isMuted: boolean;
    readonly incomingCall: IncomingBrowserCall | null;
    readonly answeredElsewhereCall: IncomingBrowserCall | null;
    readonly isBrowserRingEnabled: boolean;
    readonly isBrowserRingReady: boolean;
    readonly isBrowserAvailabilityEnabled: boolean;
    readonly isAvailableForIncomingCalls: boolean;
    readonly isBrowserAvailabilityPublished: boolean;
    readonly onAvailableForIncomingCallsChange: (available: boolean) => void;
    readonly onAcceptIncomingCall: () => void;
    readonly onRejectIncomingCall: () => void;
    readonly onHangUp: () => void;
    readonly onToggleMute: () => void;
};

// Presentational softphone card, split from the connected component so call
// states can be rendered (and visually verified) without a live call.
export function InCallPanelView({
    status,
    caregiverName,
    durationS,
    isMuted,
    incomingCall,
    answeredElsewhereCall,
    isBrowserRingEnabled,
    isBrowserRingReady,
    isBrowserAvailabilityEnabled,
    isAvailableForIncomingCalls,
    isBrowserAvailabilityPublished,
    onAvailableForIncomingCallsChange,
    onAcceptIncomingCall,
    onRejectIncomingCall,
    onHangUp,
    onToggleMute,
}: InCallPanelViewProps) {
    if (status === 'idle' && !incomingCall && !answeredElsewhereCall) {
        if (isBrowserAvailabilityEnabled) {
            return (
                <BrowserRingAvailabilityControl
                    isAvailable={isAvailableForIncomingCalls}
                    isBrowserRingReady={isBrowserRingReady}
                    isPublishedAvailable={isBrowserAvailabilityPublished}
                    onAvailableChange={onAvailableForIncomingCallsChange}
                />
            );
        }
        if (isBrowserRingEnabled) {
            return (
                <div
                    aria-label="Browser ring readiness"
                    data-browser-ring-ready={isBrowserRingReady}
                    role="status"
                />
            );
        }
        return null;
    }
    if (answeredElsewhereCall && !incomingCall && status === 'idle') {
        const callerName =
            answeredElsewhereCall.callerLabel ??
            formatPhoneNumber(answeredElsewhereCall.fromNumberDisplay);
        return (
            <div
                aria-live="polite"
                className="bg-background fixed right-4 bottom-4 z-[100] w-80 rounded-xl border p-4 shadow-xl"
                data-browser-ring-ready={isBrowserRingReady}
            >
                <div className="flex items-center gap-3">
                    <div className="bg-muted flex h-11 w-11 shrink-0 items-center justify-center rounded-full">
                        <Phone className="text-muted-foreground h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-foreground font-semibold">
                            Answered in another tab
                        </p>
                        <p className="text-muted-foreground truncate text-sm">
                            {callerName}
                        </p>
                    </div>
                </div>
            </div>
        );
    }
    if (incomingCall) {
        const callerName =
            incomingCall.callerLabel ??
            formatPhoneNumber(incomingCall.fromNumberDisplay);
        const callerNumber = formatPhoneNumber(incomingCall.fromNumberDisplay);
        const showCallerNumber = callerName !== callerNumber;
        return (
            <div
                className="bg-background fixed right-4 bottom-4 z-[100] w-80 rounded-xl border p-4 shadow-xl"
                data-browser-ring-ready={isBrowserRingReady}
            >
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
                        <Phone className="h-5 w-5 text-green-700 dark:text-green-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-muted-foreground text-xs">
                            Incoming call
                        </p>
                        <p className="text-foreground font-semibold">
                            {callerName}
                        </p>
                        {showCallerNumber ? (
                            <p className="text-muted-foreground truncate text-sm">
                                {callerNumber}
                            </p>
                        ) : null}
                        {incomingCall.lineLabel ? (
                            <p className="text-muted-foreground truncate text-sm">
                                Line:{' '}
                                {formatPhoneNumber(incomingCall.lineLabel)}
                            </p>
                        ) : null}
                    </div>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2 border-t pt-3">
                    <Button
                        onClick={onRejectIncomingCall}
                        size="sm"
                        type="button"
                        variant="outline"
                    >
                        <PhoneOff className="mr-2 h-4 w-4" />
                        Decline
                    </Button>
                    <Button
                        className="bg-green-600 text-white hover:bg-green-700"
                        onClick={onAcceptIncomingCall}
                        size="sm"
                        type="button"
                    >
                        <Phone className="mr-2 h-4 w-4" />
                        Accept
                    </Button>
                </div>
            </div>
        );
    }
    const isConnecting = status === 'connecting';

    return (
        <div
            className="bg-background fixed right-4 bottom-4 z-[100] w-80 rounded-xl border p-4 shadow-xl"
            data-browser-ring-ready={isBrowserRingReady}
        >
            <div className="flex items-center gap-3">
                <div
                    className={
                        isConnecting
                            ? 'bg-muted flex h-11 w-11 shrink-0 animate-pulse items-center justify-center rounded-full'
                            : 'flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50'
                    }
                >
                    <Phone
                        className={
                            isConnecting
                                ? 'text-muted-foreground h-5 w-5'
                                : 'h-5 w-5 text-green-700 dark:text-green-300'
                        }
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate font-semibold">
                        {caregiverName ?? 'Caregiver'}
                    </p>
                    <div
                        aria-live="polite"
                        className="text-muted-foreground flex items-center gap-1.5 text-sm"
                    >
                        {isConnecting ? (
                            'Calling…'
                        ) : (
                            <span className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-green-500" />
                                {formatDuration(durationS)}
                                {isMuted ? ' · Muted' : ''}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2 border-t pt-3">
                <Button
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                    disabled={status !== 'active'}
                    onClick={onToggleMute}
                    size="icon"
                    type="button"
                    variant={isMuted ? 'secondary' : 'outline'}
                >
                    {isMuted ? (
                        <MicOff className="h-4 w-4" />
                    ) : (
                        <Mic className="h-4 w-4" />
                    )}
                </Button>
                <Button
                    onClick={onHangUp}
                    size="sm"
                    type="button"
                    variant="destructive"
                >
                    <PhoneOff className="mr-2 h-4 w-4" />
                    {isConnecting ? 'Cancel' : 'Hang up'}
                </Button>
            </div>
        </div>
    );
}

// Floating softphone panel for browser calls. Rendered once by CallProvider
// so it survives route navigation.
export function InCallPanel() {
    const {
        status,
        caregiverName,
        startedAtMs,
        isMuted,
        incomingCall,
        answeredElsewhereCall,
        isBrowserRingEnabled,
        isBrowserRingReady,
        isBrowserAvailabilityEnabled,
        isAvailableForIncomingCalls,
        isBrowserAvailabilityPublished,
        setAvailableForIncomingCalls,
        acceptIncomingCall,
        rejectIncomingCall,
        hangUp,
        toggleMute,
    } = useCall();
    const durationS = useCallDurationS(startedAtMs);

    return (
        <InCallPanelView
            status={status}
            caregiverName={caregiverName}
            durationS={durationS}
            isMuted={isMuted}
            incomingCall={incomingCall}
            answeredElsewhereCall={answeredElsewhereCall}
            isBrowserRingEnabled={isBrowserRingEnabled}
            isBrowserRingReady={isBrowserRingReady}
            isBrowserAvailabilityEnabled={isBrowserAvailabilityEnabled}
            isAvailableForIncomingCalls={isAvailableForIncomingCalls}
            isBrowserAvailabilityPublished={isBrowserAvailabilityPublished}
            onAvailableForIncomingCallsChange={setAvailableForIncomingCalls}
            onAcceptIncomingCall={() => void acceptIncomingCall()}
            onRejectIncomingCall={rejectIncomingCall}
            onHangUp={hangUp}
            onToggleMute={toggleMute}
        />
    );
}
